import type {SvgIconComponent} from "@mui/icons-material";
import {Box, Tooltip, useTheme} from "@mui/material";
import type {MouseEventHandler, ReactNode} from "react";
import {Link as RouterLink} from "react-router-dom";
import {
    SIDEBAR_ACTION_ICON_SLOT_WIDTH,
    SIDEBAR_BUTTON_BORDER,
    SIDEBAR_BUTTON_BORDER_RADIUS,
} from "./sidebarLayout";

interface SidebarNavButtonBaseProps {
    active?: boolean;
    ariaControls?: string;
    ariaExpanded?: boolean | "true" | "false";
    ariaHaspopup?: boolean | "true";
    ariaLabel?: string;
    expanded: boolean;
    Icon?: SvgIconComponent;
    id: string;
    label: string;
    labelFontWeight?: number;
    labelMaxWidth?: number;
    leading?: ReactNode;
    /** Greys the label out (e.g. locked cross-sell apps); ignored while active. */
    muted?: boolean;
    tooltipTitle?: string;
    trailing?: ReactNode;
}

type SidebarNavButtonProps = SidebarNavButtonBaseProps & (
    | {
        external?: boolean;
        link: string;
        onClick?: never;
    }
    | {
        external?: never;
        link?: never;
        onClick: MouseEventHandler<HTMLButtonElement>;
    }
);

export function SidebarNavButton(props: SidebarNavButtonProps) {
    const {
        active = false,
        ariaControls,
        ariaExpanded,
        ariaHaspopup,
        ariaLabel,
        expanded,
        Icon,
        id,
        label,
        labelFontWeight,
        labelMaxWidth = 160,
        leading,
        muted = false,
        tooltipTitle = label,
        trailing,
    } = props;
    const theme = useTheme();
    const itemSx = {
        width: "100%",
        maxWidth: expanded ? "none" : SIDEBAR_ACTION_ICON_SLOT_WIDTH,
        minHeight: 44,
        px: 0,
        py: 0.5,
        border: SIDEBAR_BUTTON_BORDER,
        borderColor: active ? "primary.main" : "transparent",
        borderRadius: SIDEBAR_BUTTON_BORDER_RADIUS,
        backgroundColor: active ? "primary.main" : "transparent",
        color: active ? "primary.contrastText" : "primary.dark",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: expanded ? 1 : 0,
        font: "inherit",
        cursor: "pointer",
        boxSizing: "border-box",
        textAlign: "left",
        textDecoration: "none",
        overflow: "hidden",
        transition: "none",
        "&:hover": {
            backgroundColor: active ? "primary.dark" : "action.hover",
            borderColor: active ? "primary.dark" : "transparent",
        },
        "&:focus-visible": {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
        },
    };

    const content = (
        <>
            <Box
                sx={{
                    width: SIDEBAR_ACTION_ICON_SLOT_WIDTH,
                    height: 36,
                    borderRadius: SIDEBAR_BUTTON_BORDER_RADIUS,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {leading ?? (Icon ? <Icon sx={{fontSize: 22}}/> : null)}
            </Box>

            <Box
                sx={{
                    minWidth: 0,
                    maxWidth: expanded ? labelMaxWidth : 0,
                    opacity: expanded ? (muted && !active ? 0.6 : 1) : 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: 14,
                    fontWeight: labelFontWeight ?? (active ? 700 : 600),
                    lineHeight: 1.2,
                }}
            >
                {label}
            </Box>

            {trailing}
        </>
    );

    const accessibilityProps = {
        id,
        "aria-controls": ariaControls,
        "aria-current": active ? "page" as const : undefined,
        "aria-expanded": ariaExpanded,
        "aria-haspopup": ariaHaspopup,
        "aria-label": ariaLabel ?? (expanded ? undefined : label),
    };

    let button: ReactNode;

    if (props.onClick) {
        button = (
            <Box
                component="button"
                type="button"
                onClick={props.onClick}
                sx={itemSx}
                {...accessibilityProps}
            >
                {content}
            </Box>
        );
    } else if (props.external) {
        button = (
            <Box
                component="a"
                href={props.link}
                target="_blank"
                rel="noopener noreferrer"
                sx={itemSx}
                {...accessibilityProps}
            >
                {content}
            </Box>
        );
    } else {
        button = (
            <Box
                component={RouterLink}
                to={props.link}
                sx={itemSx}
                {...accessibilityProps}
            >
                {content}
            </Box>
        );
    }

    // A stable wrapper preserves mouse-leave events while the sidebar expands.
    return (
        <Tooltip disableInteractive placement="right" title={expanded ? "" : tooltipTitle}>
            {button}
        </Tooltip>
    );
}
